import { Injectable } from '@angular/core';
import { Observable, Subject, map, of, take } from 'rxjs';
import { State } from 'src/app/auth/models/user.model';
import { SchoolLocations } from 'src/app/pages/room-and-location-management/models';
import { LocationService } from 'src/app/pages/room-and-location-management/pages/location/services';
import { RevenueCategory } from 'src/app/pages/settings/pages/revenue-categories/models';
import { RevenueCategoriesService } from 'src/app/pages/settings/pages/revenue-categories/services';
import { Instrument } from 'src/app/request-information/models';
import { InstrumentsService } from 'src/app/request-information/services';
import { InstructorList } from 'src/app/schedule-introductory-lesson/models';
import { InstructorService } from 'src/app/schedule-introductory-lesson/services';
import { API_URL } from '../constants/api-url.constants';
import { CBGetResponse, CBResponse } from '../models';
import { AddressService } from './address.service';

@Injectable({ providedIn: 'root' })
export class CommonService {
  isApiCallInProgress$: Subject<boolean> = new Subject();
  instruments!: CBResponse<Instrument>;
  locations!: CBResponse<SchoolLocations>;
  instructors!: CBResponse<InstructorList>;
  revenueCategories!: CBResponse<RevenueCategory>;
  states!: CBGetResponse<Array<State>>;

  constructor(
    private instrumentsService: InstrumentsService,
    private readonly locationService: LocationService,
    private readonly instructorService: InstructorService,
    private readonly revenueCategoriesService: RevenueCategoriesService,
    private readonly addressService: AddressService
  ) {}

  getInstruments(): Observable<CBResponse<Instrument>> {
    if (!this.instruments?.result?.items?.length) {
      return this.instrumentsService.getList<CBResponse<Instrument>>(API_URL.crud.getAll).pipe(
        take(1),
        map((res) => {
          this.instruments = res as CBResponse<Instrument>;
          return res;
        })
      );
    }
    return of(this.instruments);
  }

  getLocations(): Observable<CBResponse<SchoolLocations>> {
    if (!this.locations?.result?.items?.length) {
      return this.locationService.add({}, API_URL.crud.getAll).pipe(
        take(1),
        map((res) => {
          this.locations = res as CBResponse<SchoolLocations>;
          return res;
        })
      );
    }
    return of(this.locations);
  }

  clearLocationsCache(): void {
    this.locations = {
      result: {
        totalCount: 0,
        items: []
      }
    };
  }

  getInstructors(): Observable<CBResponse<InstructorList>> {
    if (!this.instructors?.result?.items?.length) {
      return this.instructorService.add({ page: 1 }, `${API_URL.crud.getAll}`).pipe(
        take(1),
        map((res) => {
          this.instructors = res as CBResponse<InstructorList>;
          return res;
        })
      );
    }
    return of(this.instructors);
  }

  getRevenueCategories(): Observable<CBResponse<RevenueCategory>> {
    if (!this.revenueCategories?.result?.items?.length) {
      return this.revenueCategoriesService.getListWithFilters<CBResponse<RevenueCategory>>({}, `${API_URL.crud.getAll}`).pipe(
        take(1),
        map((res) => {
          this.revenueCategories = res;
          return res;
        })
      );
    }
    return of(this.revenueCategories);
  }

  getStates(): Observable<CBGetResponse<Array<State>>> {
    if (!this.states?.result.length) {
      return this.addressService.getList<CBGetResponse<Array<State>>>(API_URL.address.getStates).pipe(
        take(1),
        map((res) => {
          this.states = res;
          return res;
        })
      );
    }
    return of(this.states);
  }
}
